import Trans from '@/components/shared/Trans';
import translations from '@/components/Company/fileManager/FileManager.trans.json';
import { ENTITY_FILE_ITEM_TYPE, QUICK_FILE_TYPE } from './entityFileTypes';

export const FILE_MANAGER_PAGE_SIZE = 20;
export const FILE_MANAGER_MIN_SEARCH_LENGTH = 3;

export function getEffectiveSearchQuery(searchQuery = '') {
  const trimmed = searchQuery.trim();

  return trimmed.length >= FILE_MANAGER_MIN_SEARCH_LENGTH ? trimmed : '';
}

export function mapAncestorsToFolderTrail(ancestors, fallbackFolder) {
  const ancestorItems = ancestors ?? [];
  const trail = ancestorItems
    .filter((item) => item?.id != null && item?.name)
    .map((item) => ({ id: item.id, name: item.name }));

  if (fallbackFolder?.id == null || !fallbackFolder?.name) {
    return trail;
  }

  const lastSegment = trail[trail.length - 1];

  if (!lastSegment || lastSegment.id !== fallbackFolder.id) {
    return [...trail, { id: fallbackFolder.id, name: fallbackFolder.name }];
  }

  return trail;
}

export function buildBreadcrumbFromTrail(folderTrail, rootLabel) {
  return [
    { id: null, label: rootLabel },
    ...folderTrail.map((folder) => ({ id: folder.id, label: folder.name })),
  ];
}

export function getLocationLabelFromTrail(folderTrail, rootLabel) {
  if (!folderTrail.length) {
    return rootLabel;
  }

  return folderTrail[folderTrail.length - 1].name;
}

export function getLocationPathFromTrail(folderTrail, rootLabel) {
  const segments = [rootLabel, ...folderTrail.map((folder) => folder.name)];

  return segments.join(' / ');
}

const FILE_ICON_TYPES_BY_EXTENSION = {
  pdf: 'pdf',
  xls: 'xls',
  xlsx: 'xls',
  xlsm: 'xls',
  xlsb: 'xls',
  csv: 'xls',
  ods: 'xls',
  doc: 'docx',
  docx: 'docx',
  odt: 'docx',
  rtf: 'docx',
  txt: 'docx',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  bmp: 'image',
  tif: 'image',
  tiff: 'image',
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  mkv: 'video',
  webm: 'video',
  mpeg: 'video',
  mpg: 'video',
  mp3: 'audio',
  wav: 'audio',
  ogg: 'audio',
  m4a: 'audio',
  aac: 'audio',
  flac: 'audio',
};

function formatChildCount(count) {
  const value = count ?? 0;
  const key = value === 1 ? 'FileManagerFileCount' : 'FileManagerFilesCount';

  return Trans(translations, key, String(value));
}

function resolveFileIconType(contentType) {
  const extension = String(contentType || '')
    .trim()
    .toLowerCase()
    .replace(/^\./, '');

  return FILE_ICON_TYPES_BY_EXTENSION[extension] ?? 'file';
}

export const FILE_PREVIEW_MODE = {
  IMAGE: 'image',
  PDF: 'pdf',
  TEXT: 'text',
  CSV: 'csv',
};

export const FILE_PREVIEW_MAX_BYTES = 20 * 1024 * 1024;

// SVG is intentionally excluded: a same-origin blob SVG can run scripts when
// opened as a page (e.g. "Open image in new tab").
const FILE_PREVIEW_MODE_BY_EXTENSION = {
  jpg: FILE_PREVIEW_MODE.IMAGE,
  jpeg: FILE_PREVIEW_MODE.IMAGE,
  png: FILE_PREVIEW_MODE.IMAGE,
  gif: FILE_PREVIEW_MODE.IMAGE,
  webp: FILE_PREVIEW_MODE.IMAGE,
  bmp: FILE_PREVIEW_MODE.IMAGE,
  pdf: FILE_PREVIEW_MODE.PDF,
  txt: FILE_PREVIEW_MODE.TEXT,
  csv: FILE_PREVIEW_MODE.CSV,
};

const FILE_PREVIEW_MIME_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
};

function normalizeExtension(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^\./, '');
}

// List items carry the extension in typeLabel, detail items in contentType.
export function getFileExtension(item) {
  return normalizeExtension(item?.contentType ?? item?.typeLabel);
}

export function getFilePreviewMimeType(extension) {
  return (
    FILE_PREVIEW_MIME_BY_EXTENSION[normalizeExtension(extension)] ??
    'application/octet-stream'
  );
}

export function getFilePreviewMode(item) {
  // Folders and links have a translated typeLabel, never an extension.
  if (!item?.fileUrl || item.type === 'folder' || item.type === 'link') {
    return null;
  }

  return FILE_PREVIEW_MODE_BY_EXTENSION[getFileExtension(item)] ?? null;
}

// Same icon type as the listing, for a local file not uploaded yet.
export function resolveFileIconTypeFromName(name) {
  const parts = String(name || '').split('.');

  return parts.length > 1 ? resolveFileIconType(parts.pop()) : 'file';
}

export const FILE_TYPE_ICONS = {
  folder: 'icon-carpeta',
  pdf: 'icon-pdf',
  xls: 'icon-excel',
  docx: 'icon-archivos',
  image: 'icon-imagenes',
  video: 'icon-video',
  audio: 'icon-audio',
  file: 'icon-archivos',
  link: 'icon-link',
};

export function getFileTypeIcon(type) {
  return FILE_TYPE_ICONS[type] ?? FILE_TYPE_ICONS.file;
}

function mapQuickFileItem({ file, is_public: isPublic }) {
  const isLink = file.type === ENTITY_FILE_ITEM_TYPE.LINK;

  return {
    id: file.id,
    name: file.name,
    type: isLink ? QUICK_FILE_TYPE.LINK : QUICK_FILE_TYPE.FILE,
    url: (isLink ? file.link_url : file.file_url) ?? '',
    icon: isLink
      ? FILE_TYPE_ICONS.link
      : getFileTypeIcon(resolveFileIconType(file.content_type)),
    in_web: Boolean(isPublic),
  };
}

// Legacy quick files consumers (Quick Display, Sender) expect `{ files: [...] }`
// with multiuploader-like keys. FOLDER items are always excluded; LINK items are
// opt-in because the Sender attaches these rows to emails and links cannot be attached.
export function mapEntityFileItemsToQuickFiles(
  entityFileItems,
  { includeLinks = false } = {}
) {
  const allowedTypes = includeLinks
    ? [ENTITY_FILE_ITEM_TYPE.FILE, ENTITY_FILE_ITEM_TYPE.LINK]
    : [ENTITY_FILE_ITEM_TYPE.FILE];
  const files = (entityFileItems ?? [])
    .filter((item) => allowedTypes.includes(item?.file?.type))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(mapQuickFileItem);

  return { files };
}

export const LINKED_ENTITY_TYPE = {
  PROPERTY: 'PROPERTY',
  CONTACT: 'CONTACT',
  DEVELOPMENT: 'DEVELOPMENT',
};

export const LINKED_ENTITY_KEY_BY_TYPE = {
  [LINKED_ENTITY_TYPE.PROPERTY]: 'properties',
  [LINKED_ENTITY_TYPE.CONTACT]: 'contacts',
  [LINKED_ENTITY_TYPE.DEVELOPMENT]: 'developments',
};

export const LINKED_ENTITY_TYPE_BY_KEY = {
  properties: LINKED_ENTITY_TYPE.PROPERTY,
  contacts: LINKED_ENTITY_TYPE.CONTACT,
  developments: LINKED_ENTITY_TYPE.DEVELOPMENT,
};

export const LINKED_FIELD_BY_ENTITY_KEY = {
  properties: 'linkedProperties',
  developments: 'linkedDevelopments',
  contacts: 'linkedContacts',
};

export function mapEntityKeyToApiType(entityKey) {
  return LINKED_ENTITY_TYPE_BY_KEY[entityKey] ?? null;
}

export function mapApiEntityTypeToUrlSegment(entityType) {
  if (!entityType) {
    return null;
  }

  return String(entityType).toLowerCase();
}

export function buildLinkedEntityRefKey(entity) {
  if (!entity?.entity_type || entity.entity_id == null) {
    return null;
  }

  return `${entity.entity_type}:${entity.entity_id}`;
}

export function mergeLinkedEntityRefs(...entityGroups) {
  const mergedByKey = new Map();

  entityGroups.flat().forEach((entity) => {
    const key = buildLinkedEntityRefKey(entity);

    if (!key) {
      return;
    }

    const existing = mergedByKey.get(key);

    if (!existing) {
      mergedByKey.set(key, entity);
      return;
    }

    mergedByKey.set(key, {
      ...existing,
      ...entity,
      name: existing.name || entity.name,
      is_public: entity.is_public ?? existing.is_public,
      role: entity.role ?? existing.role,
    });
  });

  return Array.from(mergedByKey.values());
}

export function filterUnlinkedEntitySearchResults(
  results = [],
  linkedItems = []
) {
  const filteredResults = results.filter(
    (item) => !linkedItems.some((linkedItem) => linkedItem?.id === item?.id)
  );

  return filteredResults.length
    ? filteredResults
    : [{ noData: 'NoResultsFound' }];
}

export function getLinkedEntityDisplayName(entityKey, item) {
  if (entityKey === 'properties') {
    return item?.address ?? '';
  }

  return item?.name ?? '';
}

export function buildApiLinkedEntity({ entityKey, item, showInWeb }) {
  const entityType = mapEntityKeyToApiType(entityKey);
  const name = getLinkedEntityDisplayName(entityKey, item);
  const apiEntity = {
    entity_type: entityType,
    entity_id: item.id,
    name,
  };

  if (entityKey === 'properties' || entityKey === 'developments') {
    apiEntity.is_public = Boolean(showInWeb);
  }

  return apiEntity;
}

export function buildFileItemEntitiesPayload({
  linkedProperties = [],
  linkedDevelopments = [],
  linkedContacts = [],
}) {
  const propertyEntities = linkedProperties
    .filter((item) => item?.id != null)
    .map((item) => ({
      entity_type: LINKED_ENTITY_TYPE.PROPERTY,
      entity_id: item.id,
      is_public: Boolean(item.showInWeb),
    }));

  const developmentEntities = linkedDevelopments
    .filter((item) => item?.id != null)
    .map((item) => ({
      entity_type: LINKED_ENTITY_TYPE.DEVELOPMENT,
      entity_id: item.id,
      is_public: Boolean(item.showInWeb),
    }));

  const contactEntities = linkedContacts
    .filter((item) => item?.id != null)
    .map((item) => ({
      entity_type: LINKED_ENTITY_TYPE.CONTACT,
      entity_id: item.id,
    }));

  return [...propertyEntities, ...developmentEntities, ...contactEntities];
}

export function normalizeLinkedEntities(linkedEntities) {
  if (!Array.isArray(linkedEntities)) {
    return [];
  }

  return linkedEntities.filter(
    (entity) =>
      entity &&
      (entity.entity_type === LINKED_ENTITY_TYPE.PROPERTY ||
        entity.entity_type === LINKED_ENTITY_TYPE.CONTACT ||
        entity.entity_type === LINKED_ENTITY_TYPE.DEVELOPMENT) &&
      entity.entity_id != null &&
      typeof entity.name === 'string'
  );
}

export function pickLinkedEntityAnchor(linkedEntityRefs = []) {
  const normalized = normalizeLinkedEntities(linkedEntityRefs);

  return (
    normalized.find(
      (entity) => entity.entity_type === LINKED_ENTITY_TYPE.PROPERTY
    ) ||
    normalized.find(
      (entity) => entity.entity_type === LINKED_ENTITY_TYPE.DEVELOPMENT
    ) ||
    normalized[0] ||
    null
  );
}

export function hasLinkedEntities(linkedEntities) {
  return normalizeLinkedEntities(linkedEntities).length > 0;
}

export function mapEnrichedLinkedEntitiesToApiRefs(entities = []) {
  return normalizeLinkedEntities(
    entities.map((entity) => {
      const apiEntity = {
        entity_type: entity.entity_type,
        entity_id: entity.entity_id,
        name: entity.name,
      };

      if (entity.entity_type !== LINKED_ENTITY_TYPE.CONTACT) {
        apiEntity.is_public = Boolean(entity.is_public);
      }

      return apiEntity;
    })
  );
}

export function normalizeCreatedBy(createdBy) {
  if (!createdBy) {
    return null;
  }

  if (typeof createdBy === 'object' && createdBy.id != null) {
    return {
      id: createdBy.id,
      name: createdBy.name,
    };
  }

  return null;
}

export function mapLinkedEntitiesToModalState(linkedEntities = []) {
  const normalized = normalizeLinkedEntities(linkedEntities);
  const linkedProperties = [];
  const linkedDevelopments = [];
  const linkedContacts = [];

  normalized.forEach((entity) => {
    if (entity.entity_type === LINKED_ENTITY_TYPE.PROPERTY) {
      linkedProperties.push({
        id: entity.entity_id,
        address: entity.name,
        showInWeb: Boolean(entity.is_public),
        role: entity.role ?? '',
      });
      return;
    }

    if (entity.entity_type === LINKED_ENTITY_TYPE.DEVELOPMENT) {
      linkedDevelopments.push({
        id: entity.entity_id,
        name: entity.name,
        showInWeb: Boolean(entity.is_public),
        role: entity.role ?? '',
      });
      return;
    }

    if (entity.entity_type === LINKED_ENTITY_TYPE.CONTACT) {
      linkedContacts.push({
        id: entity.entity_id,
        name: entity.name,
      });
    }
  });

  return {
    linkedProperties,
    linkedDevelopments,
    linkedContacts,
  };
}

export function buildDeleteItemsPayload(items = []) {
  const ids = items.map((item) => item.id);

  if (items.length !== 1) {
    return { ids, kind: 'bulk' };
  }

  const [{ type }] = items;

  if (type === 'folder' || type === 'link') {
    return { ids, kind: type };
  }

  return { ids, kind: 'file' };
}

export function mapAncestorFoldersToLocationTrail(ancestors = []) {
  return (ancestors ?? [])
    .filter(
      (folder) =>
        folder?.id != null &&
        folder?.name &&
        (folder.type === 'FOLDER' || folder.type == null)
    )
    .map((folder) => ({ id: folder.id, name: folder.name }));
}

export function mapFileItem(apiItem) {
  const linkedEntities = normalizeLinkedEntities(apiItem.linked_entities);

  const base = {
    id: apiItem.id,
    name: apiItem.name,
    parentId: apiItem.parent ?? null,
    created_by: apiItem.created_by,
    created_at: apiItem.created_at,
    typeLabel: apiItem.content_type,
    linkedEntities,
    size: apiItem.size,
  };

  if (apiItem.type === 'FOLDER') {
    return {
      ...base,
      type: 'folder',
      isSystem: Boolean(apiItem.is_system),
      typeLabel: Trans(translations, 'FileManagerTypeFolder'),
      size: formatChildCount(apiItem.child_count),
    };
  }

  if (apiItem.type === 'LINK') {
    return {
      ...base,
      type: 'link',
      typeLabel: Trans(translations, 'FileManagerTypeLink'),
      size: Trans(translations, 'FileManagerEmptySize'),
    };
  }

  return {
    ...base,
    type: resolveFileIconType(apiItem.content_type),
    fileUrl: apiItem.file_url ?? null,
  };
}

export function mapFileItemDetail(apiItem) {
  const base = mapFileItem(apiItem);
  const createdBy = normalizeCreatedBy(apiItem.created_by);

  return {
    ...base,
    description: apiItem.description ?? '',
    linkUrl: apiItem.link_url ?? null,
    fileUrl: apiItem.file_url ?? null,
    created_by: createdBy,
    contentType: apiItem.content_type ?? '',
    fileSize: apiItem.size,
  };
}

export function buildBreadcrumbPath(items, currentFolderId, rootLabel) {
  const path = [{ id: null, label: rootLabel }];

  if (currentFolderId == null) {
    return path;
  }

  const itemsById = new Map(items.map((item) => [item.id, item]));
  const segments = [];
  let folderId = currentFolderId;

  while (folderId != null) {
    const folder = itemsById.get(folderId);

    if (!folder) {
      return path;
    }

    segments.unshift({ id: folder.id, label: folder.name });
    folderId = folder.parentId ?? null;
  }

  return [...path, ...segments];
}

export function filterItemsByParentId(items, parentId) {
  return items.filter((item) => (item.parentId ?? null) === parentId);
}

function parseAuthorDate(authorDate) {
  if (!authorDate) {
    return 0;
  }

  const [datePart, timePart = '00:00'] = authorDate.split(' · ');
  const [day, month, year] = datePart.split('/');

  if (!day || !month || !year) {
    return 0;
  }

  return new Date(`${year}-${month}-${day}T${timePart}`).getTime() || 0;
}

export function getCurrentLocationLabel(items, currentFolderId, rootLabel) {
  if (currentFolderId == null) {
    return rootLabel;
  }

  const folder = items.find((item) => item.id === currentFolderId);

  return folder?.name ?? rootLabel;
}

export const SORT_BY = {
  UPLOAD_DATE: 'uploadDate',
  FILE_NAME: 'name',
};

export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
};

export const FILE_MANAGER_MATCH_MODE = {
  ANY: 'any',
  ALL: 'all',
};

export function buildOrderBy(sortBy, sortDirection) {
  const field = sortBy === SORT_BY.FILE_NAME ? 'name' : 'created_at';
  const prefix = sortDirection === SORT_DIRECTION.DESC ? '-' : '';

  return `${prefix}${field}`;
}

function hasUploadDateFilter(uploadDateRange) {
  const [startDate, endDate] = uploadDateRange ?? [null, null];
  return Boolean(startDate) || Boolean(endDate);
}

export function formatFileManagerDateParam(date) {
  if (!date) {
    return null;
  }

  const parsed = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function hasActiveFileManagerFilters({
  searchQuery = '',
  uploadedBy = [],
  uploadDateRange = [null, null],
  linkedContacts = [],
  linkedProperties = [],
  linkedDevelopments = [],
}) {
  return (
    Boolean(getEffectiveSearchQuery(searchQuery)) ||
    uploadedBy.length > 0 ||
    hasUploadDateFilter(uploadDateRange) ||
    linkedContacts.length > 0 ||
    linkedProperties.length > 0 ||
    linkedDevelopments.length > 0
  );
}

export function buildFileManagerSearchParams({
  searchQuery = '',
  uploadedBy = [],
  uploadDateRange = [null, null],
  linkedContacts = [],
  linkedProperties = [],
  linkedDevelopments = [],
  matchMode = FILE_MANAGER_MATCH_MODE.ANY,
  sortBy,
  sortDirection,
  page = 1,
  pageSize = FILE_MANAGER_PAGE_SIZE,
}) {
  const [startDate, endDate] = uploadDateRange ?? [null, null];
  const effectiveQuery = getEffectiveSearchQuery(searchQuery);
  const createdBy = uploadedBy.map((agent) => agent.id).join(',');
  const propertyIds = linkedProperties.map((property) => property.id).join(',');
  const contactIds = linkedContacts.map((contact) => contact.id).join(',');
  const developmentIds = linkedDevelopments
    .map((development) => development.id)
    .join(',');
  const hasLinkedEntityIds =
    Boolean(propertyIds) || Boolean(contactIds) || Boolean(developmentIds);

  return {
    q: effectiveQuery || undefined,
    dateFrom: formatFileManagerDateParam(startDate) || undefined,
    dateTo: formatFileManagerDateParam(endDate) || undefined,
    createdBy: createdBy || undefined,
    propertyIds: propertyIds || undefined,
    contactIds: contactIds || undefined,
    developmentIds: developmentIds || undefined,
    matchMode:
      hasLinkedEntityIds && matchMode !== FILE_MANAGER_MATCH_MODE.ANY
        ? matchMode
        : undefined,
    orderBy: buildOrderBy(sortBy, sortDirection),
    page,
    pageSize,
  };
}

export function buildListQueryKey({
  currentFolderId,
  sortBy,
  sortDirection,
  searchQuery = '',
  uploadedBy = [],
  uploadDateRange = [null, null],
  linkedContacts = [],
  linkedProperties = [],
  linkedDevelopments = [],
  matchMode = FILE_MANAGER_MATCH_MODE.ANY,
  page = 1,
}) {
  const [startDate, endDate] = uploadDateRange ?? [null, null];

  return {
    parentId: currentFolderId,
    sortBy,
    sortDirection,
    page,
    searchQuery: getEffectiveSearchQuery(searchQuery),
    uploadedByIds: uploadedBy.map((agent) => agent.id).join(','),
    uploadDateFrom: formatFileManagerDateParam(startDate) ?? null,
    uploadDateTo: formatFileManagerDateParam(endDate) ?? null,
    linkedContactIds: linkedContacts.map((contact) => contact.id).join(','),
    linkedPropertyIds: linkedProperties
      .map((property) => property.id)
      .join(','),
    linkedDevelopmentIds: linkedDevelopments
      .map((development) => development.id)
      .join(','),
    matchMode,
  };
}

function getSortValue(item, sortBy) {
  if (sortBy === SORT_BY.FILE_NAME) {
    return item.name ?? '';
  }

  return parseAuthorDate(item.authorDate);
}

export const FILE_MANAGER_FILTER_ENTITY = {
  CONTACT: 'contact',
  PROPERTY: 'property',
  DEVELOPMENT: 'development',
};

export function buildFileManagerFilterTags({
  linkedContacts = [],
  linkedProperties = [],
  linkedDevelopments = [],
}) {
  const contactTags = linkedContacts.map((contact) => ({
    id: `${FILE_MANAGER_FILTER_ENTITY.CONTACT}-${contact.id}`,
    text: contact.name,
    type: FILE_MANAGER_FILTER_ENTITY.CONTACT,
    entityId: contact.id,
  }));

  const propertyTags = linkedProperties.map((property) => ({
    id: `${FILE_MANAGER_FILTER_ENTITY.PROPERTY}-${property.id}`,
    text: property.address || property.title || property.ref_code || '',
    type: FILE_MANAGER_FILTER_ENTITY.PROPERTY,
    entityId: property.id,
  }));

  const developmentTags = linkedDevelopments.map((development) => ({
    id: `${FILE_MANAGER_FILTER_ENTITY.DEVELOPMENT}-${development.id}`,
    text: development.name || development.full_address || '',
    type: FILE_MANAGER_FILTER_ENTITY.DEVELOPMENT,
    entityId: development.id,
  }));

  return [...contactTags, ...propertyTags, ...developmentTags];
}

const VIEW_MODE_TO_API = {
  list: 'list',
  grid: 'cards',
};

const VIEW_MODE_FROM_API = {
  list: 'list',
  cards: 'grid',
};

export function mapViewModeToApi(viewMode) {
  return VIEW_MODE_TO_API[viewMode] ?? null;
}

export function mapViewModeFromApi(fileManagerView) {
  return VIEW_MODE_FROM_API[fileManagerView] ?? null;
}

export function sortFileManagerItems(items, sortBy, sortDirection) {
  const direction = sortDirection === SORT_DIRECTION.ASC ? 1 : -1;

  return [...items].sort((left, right) => {
    const leftValue = getSortValue(left, sortBy);
    const rightValue = getSortValue(right, sortBy);

    if (typeof leftValue === 'string') {
      return leftValue.localeCompare(rightValue) * direction;
    }

    return (leftValue - rightValue) * direction;
  });
}

export function buildDuplicateCheckItems({ files = [], link = null }) {
  if (link) {
    return [{ type: ENTITY_FILE_ITEM_TYPE.LINK, name: link.name.trim() }];
  }

  return files.map((file) => ({
    type: ENTITY_FILE_ITEM_TYPE.FILE,
    name: file.name,
    content_type: file.type || 'application/octet-stream',
  }));
}
